import * as immutable from 'object-path-immutable';
import * as types from './actionTypes';

const isSameCombobox = (combobox, id) => {
  return String(combobox?.alias) === String(id) || String(combobox?.id) === String(id);
};

const getIndexOfComboboxToUpdate = (list, id) => {
  return list.findIndex(combobox => isSameCombobox(combobox, id));
};

const getSourceIngredients = source => {
  return [
    ...(source?.include_ingredients || []),
    ...(source?.available_ingredients || []),
    ...(source?.available_igredients || []),
  ];
};

const getLevelsIngredientMetaMap = (levels = []) => {
  const map = {};

  (levels || []).forEach(level => {
    (level?.goods || []).forEach(levelGood => {
      (levelGood?.variations || []).forEach(variation => {
        getSourceIngredients(variation).forEach(ingredient => {
          if (!ingredient?.id || map[String(ingredient.id)]) {
            return;
          }

          map[String(ingredient.id)] = ingredient;
        });
      });
    });
  });

  return map;
};

const getMatchedVariation = (good, matchedCatalogGood) => {
  const variations = matchedCatalogGood?.variations || [];

  return (
    variations.find(variation => {
      const isSameSize =
        String(variation?.size?.value || '') === String(good?.size?.value || good?.size || '');
      const isSameDough =
        String(variation?.dough?.id || variation?.dough || '') ===
        String(good?.dough?.id || good?.dough || '');
      const isSameStuffedCrust =
        String(variation?.stuffed_crust || 'none') === String(good?.stuffed_crust || 'none');

      return isSameSize && isSameDough && isSameStuffedCrust;
    }) || variations[0]
  );
};

const getCartIngredientOverrides = good => {
  const overrides = {};

  (good?.ingredients || []).forEach(ingredient => {
    if (!ingredient?.id) {
      return;
    }

    overrides[String(ingredient.id)] = Number(ingredient?.count || 0);
  });

  return overrides;
};

const getIngredientMetaMap = (good, matchedCatalogGood, levelsIngredientMetaMap = {}) => {
  const map = {};
  const sources = [good, ...(matchedCatalogGood?.variations || []), matchedCatalogGood];

  sources.forEach(source => {
    getSourceIngredients(source).forEach(ingredient => {
      if (!ingredient?.id || map[String(ingredient.id)]) {
        return;
      }

      map[String(ingredient.id)] = ingredient;
    });
  });

  return {
    ...levelsIngredientMetaMap,
    ...map,
  };
};

const getIngredientField = (ingredient, meta, field) => {
  return ingredient?.[field] ?? meta?.[field];
};

const hydrateIngredientWithMeta = (ingredient, ingredientMetaMap, included) => {
  const meta = ingredientMetaMap[String(ingredient?.id)] || ingredientMetaMap[ingredient?.id] || {};
  const rawPrice = getIngredientField(ingredient, meta, 'price');

  return {
    ...meta,
    ...ingredient,
    id: getIngredientField(ingredient, meta, 'id'),
    name: getIngredientField(ingredient, meta, 'name'),
    custom_name: getIngredientField(ingredient, meta, 'custom_name'),
    type: getIngredientField(ingredient, meta, 'type'),
    image: getIngredientField(ingredient, meta, 'image'),
    image_cart: getIngredientField(ingredient, meta, 'image_cart'),
    price: Number(rawPrice || 0),
    included: Boolean(included),
    count: Number(ingredient?.count || 0),
  };
};

const getHydratedIncludeIngredients = (good, ingredientMetaMap, matchedVariation) => {
  const removedIngredientIds = new Set(
    (good?.removed_ingredients || []).map(ingredient => String(ingredient?.id)),
  );
  const ingredientOverrides = getCartIngredientOverrides(good);
  const includeSource = (good?.include_ingredients || []).length
    ? good.include_ingredients
    : matchedVariation?.include_ingredients || [];

  return includeSource.map(ingredient => {
    const overrideCount = ingredientOverrides[String(ingredient?.id)];
    const normalizedCount =
      typeof overrideCount === 'number' ? overrideCount : Number(ingredient?.count || 1);
    const normalized = hydrateIngredientWithMeta(ingredient, ingredientMetaMap, true);
    const isRemoved =
      removedIngredientIds.has(String(ingredient?.id)) ||
      Boolean(ingredient?.removed) ||
      normalizedCount === 0;

    return {
      ...normalized,
      removed: isRemoved,
      count: isRemoved ? 0 : normalizedCount,
    };
  });
};

const getHydratedAddedIngredients = (
  good,
  ingredientMetaMap,
  hydratedIncludeIngredients,
  matchedVariation,
) => {
  if ((good?.added_ingredients || []).length > 0) {
    return good.added_ingredients.map(ingredient =>
      hydrateIngredientWithMeta(ingredient, ingredientMetaMap, ingredient?.included),
    );
  }

  const ingredientOverrides = getCartIngredientOverrides(good);
  const includeIngredients = hydratedIncludeIngredients
    .filter(ingredient => Number(ingredient?.count || 0) > 1)
    .map(ingredient => ({ ...ingredient, included: true }));

  const availableSource = (
    (good?.available_ingredients || []).length
      ? good?.available_ingredients
      : good?.available_igredients || []
  ).length
    ? good?.available_ingredients || good?.available_igredients
    : matchedVariation?.available_ingredients || matchedVariation?.available_igredients || [];

  const availableIngredients = availableSource
    .filter(ingredient => Number(ingredient?.count || 0) > 0)
    .map(ingredient => {
      const overrideCount = ingredientOverrides[String(ingredient?.id)];

      return hydrateIngredientWithMeta(
        {
          ...ingredient,
          count: typeof overrideCount === 'number' ? overrideCount : ingredient?.count,
        },
        ingredientMetaMap,
        false,
      );
    })
    .filter(ingredient => Number(ingredient?.count || 0) > 0);

  const extraIngredientsFromOverrides = Object.entries(ingredientOverrides)
    .filter(([id, count]) => {
      if (count <= 0) {
        return false;
      }

      const ingredientMeta = ingredientMetaMap[id];

      if (!ingredientMeta) {
        return false;
      }

      const alreadyIncluded = includeIngredients.some(
        ingredient => String(ingredient?.id) === String(id),
      );
      const alreadyAvailable = availableIngredients.some(
        ingredient => String(ingredient?.id) === String(id),
      );

      return !alreadyIncluded && !alreadyAvailable;
    })
    .map(([id, count]) =>
      hydrateIngredientWithMeta(
        {
          ...(ingredientMetaMap[id] || {}),
          id,
          count,
        },
        ingredientMetaMap,
        false,
      ),
    );

  return [...includeIngredients, ...availableIngredients, ...extraIngredientsFromOverrides];
};

const getHydratedRemovedIngredients = (good, ingredientMetaMap, hydratedIncludeIngredients) => {
  const removedByFlag = hydratedIncludeIngredients
    .filter(ingredient => Boolean(ingredient?.removed))
    .map(ingredient => hydrateIngredientWithMeta({ ...ingredient, count: 0 }, ingredientMetaMap, true));

  const removedFromPayload = (good?.removed_ingredients || []).map(ingredient =>
    hydrateIngredientWithMeta({ ...ingredient, count: 0 }, ingredientMetaMap, true),
  );

  const deduped = {};
  [...removedByFlag, ...removedFromPayload].forEach(ingredient => {
    if (!ingredient?.id) {
      return;
    }

    deduped[String(ingredient.id)] = ingredient;
  });

  return Object.values(deduped);
};

const hydrateGoodsFromCart = (goods = [], levels = []) => {
  const levelsIngredientMetaMap = getLevelsIngredientMetaMap(levels);

  return goods.map(good => {
    const normalizedGoodId = good.id ?? good.good_id;
    const matchedLevel = levels.find(level => {
      return String(level.id) === String(good.level_id) || String(level.level) === String(good.level);
    });
    const matchedCatalogGood = matchedLevel?.goods?.find(levelGood => {
      return String(levelGood.id) === String(normalizedGoodId);
    });
    const matchedVariation = getMatchedVariation(good, matchedCatalogGood);
    const ingredientMetaMap = getIngredientMetaMap(
      good,
      matchedCatalogGood,
      levelsIngredientMetaMap,
    );
    const hydratedIncludeIngredients = getHydratedIncludeIngredients(
      good,
      ingredientMetaMap,
      matchedVariation,
    );

    return {
      ...good,
      id: normalizedGoodId,
      image_cart:
        good.image_cart ||
        good.image ||
        matchedCatalogGood?.image_cart ||
        matchedCatalogGood?.variations?.[0]?.image_cart,
      level: matchedLevel?.level ?? good.level,
      level_id: good.level_id ?? matchedLevel?.id,
      category_id: good.category_id ?? matchedCatalogGood?.category_id,
      name: good.name ?? matchedCatalogGood?.name,
      custom_name: good.custom_name ?? matchedCatalogGood?.custom_name,
      description: good.description ?? matchedCatalogGood?.description,
      type: 'good',
      count: Number(good.count || 1),
      include_ingredients: hydratedIncludeIngredients,
      added_ingredients: getHydratedAddedIngredients(
        good,
        ingredientMetaMap,
        hydratedIncludeIngredients,
        matchedVariation,
      ),
      removed_ingredients: getHydratedRemovedIngredients(
        good,
        ingredientMetaMap,
        hydratedIncludeIngredients,
      ),
    };
  });
};

const getIndexOfLevelToUpdate = (levels, id) => {
  return levels.findIndex(level => level.level === id);
};

const getIndexOfGoodToUpdate = (goods, id) => {
  return goods.findIndex(good => good.id === id);
};

const getIndexOfVariationToUpdate = (variations, id) => {
  return variations.findIndex(variation => variation.id === id);
};

const initialState = {
  list: [],
  isLoading: false,
  isLoaded: false,
  error: '',
  boxes: {},
};

export default function comboboxReducer(state = initialState, action) {
  switch (action.type) {
    case types.FETCH_COMBOBOXES_REQUEST:
      return {
        ...state,
        isLoading: true,
        isLoaded: false,
        error: '',
      };

    case types.FETCH_COMBOBOXES_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        initialList: action.payload,
        list: action.payload,
        error: '',
      };

    case types.FETCH_COMBOBOXES_FAILURE:
      return {
        ...state,
        list: [],
        isLoaded: false,
        isLoading: false,
        error: action.payload,
      };

    //

    case types.REMOVE_INGREDIENT: {
      const indexOfComboboxToUpdate = getIndexOfComboboxToUpdate(
        state.list,
        action.payload.comboboxId,
      );

      const indexOfLevelToUpdate = getIndexOfLevelToUpdate(
        state.list[indexOfComboboxToUpdate].levels,
        action.payload.level,
      );

      const indexOfGoodToUpdate = getIndexOfGoodToUpdate(
        state.list[indexOfComboboxToUpdate].levels[indexOfLevelToUpdate].goods,
        action.payload.goodId,
      );

      const indexOfVariationToUpdate = getIndexOfVariationToUpdate(
        state.list[indexOfComboboxToUpdate].levels[indexOfLevelToUpdate].goods[indexOfGoodToUpdate]
          .variations,
        action.payload.variationId,
      );

      return immutable.update(
        state,
        [
          'list',
          indexOfComboboxToUpdate,
          'levels',
          indexOfLevelToUpdate,
          'goods',
          indexOfGoodToUpdate,
          'variations',
          indexOfVariationToUpdate,
          'include_ingredients',
        ],
        ingredients => {
          const indexOfIngredientToUpdate = ingredients.findIndex(
            ingredient => ingredient.id === action.payload.ingredientId,
          );
          return immutable.update(ingredients, [indexOfIngredientToUpdate], ingredient =>
            immutable.set(ingredient, 'removed', !ingredient.removed),
          );
        },
      );
    }

    case types.CANCEL_REMOVED_INGREDIENTS: {
      const indexOfComboboxToUpdate = getIndexOfComboboxToUpdate(
        state.list,
        action.payload.comboboxId,
      );

      const indexOfLevelToUpdate = getIndexOfLevelToUpdate(
        state.list[indexOfComboboxToUpdate].levels,
        action.payload.level,
      );

      const indexOfGoodToUpdate = getIndexOfGoodToUpdate(
        state.list[indexOfComboboxToUpdate].levels[indexOfLevelToUpdate].goods,
        action.payload.goodId,
      );

      const indexOfVariationToUpdate = getIndexOfVariationToUpdate(
        state.list[indexOfComboboxToUpdate].levels[indexOfLevelToUpdate].goods[indexOfGoodToUpdate]
          .variations,
        action.payload.variationId,
      );

      return immutable.update(
        state,
        [
          'list',
          indexOfComboboxToUpdate,
          'levels',
          indexOfLevelToUpdate,
          'goods',
          indexOfGoodToUpdate,
          'variations',
          indexOfVariationToUpdate,
          'include_ingredients',
        ],
        ingredients =>
          (ingredients || []).map(ingredient => immutable.set(ingredient, 'removed', false)),
      );
    }

    case types.ADD_TO_SELECTED_SUCCESS:
      return immutable.push(state, `boxes.${action.alias}.goods`, action.composition);

    case types.REMOVE_FROM_SELECTED_SUCCESS: {
      const goods = state.boxes[action.alias]?.goods;
      if (!goods) {
        return state;
      }

      const findIndex = goods.findIndex(good => {
        return String(good.id ?? good.good_id) === String(action.id);
      });

      if (findIndex === -1) {
        return state;
      }

      return immutable.del(state, `boxes.${action.alias}.goods.${findIndex}`);
    }

    case types.HYDRATE_COMBOBOX_FROM_CART:
      return immutable.assign(state, ['boxes', action.payload.alias], {
        goods: hydrateGoodsFromCart(action.payload.goods, action.payload.levels),
        initialGoods: hydrateGoodsFromCart(action.payload.goods, action.payload.levels),
        added: false,
        isAdding: false,
        error: '',
        isEditMode: true,
        editItemHash: action.payload.hash,
      });

    case types.SET_IS_ADDED:
      return immutable.assign(state, ['boxes', action.payload], {
        added: true,
        isAdding: false,
      });

    case types.ADD_TO_CART_REQUEST: {
      return immutable.assign(state, ['boxes', action.payload], {
        added: false,
        isAdding: true,
        error: '',
      });
    }

    case types.ADD_TO_CART_SUCCESS: {
      return immutable.assign(state, ['boxes', action.payload], {
        isAdding: false,
        added: true,
        error: '',
      });
    }

    case types.ADD_TO_CART_FAILURE: {
      return immutable.assign(state, ['boxes', action.payload.alias], {
        added: false,
        isAdding: false,
        error: action.payload.error,
      });
    }

    case types.RESET_COMBOBOX: {
      const comboboxIndex = getIndexOfComboboxToUpdate(state.list, action.payload);

      if (comboboxIndex === -1) {
        return {
          ...state,
          boxes: {},
        };
      }

      return {
        ...state,
        boxes: {},
        list: immutable.assign(state.list, [comboboxIndex], state.initialList[comboboxIndex]),
      };
    }

    default:
      return state;
  }
}
